import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { UPDATE_AUTOMATION_RULE } from '../../graphql/mutations';
import { CreateProjectModal } from '../projects/CreateProjectModal';
import { showInkToast } from '../cms/InkToast';
import { APPROVE_CAMPAIGN, channelMark, channelName, dayTime, toDate } from './reach';

/**
 * Reach → Campaigns ("TinyCMS UX optimization review v6", Prototype v6): the
 * campaigns (projects) and the always-on rules (automation rules) on the left;
 * the selected one on the right. A campaign whose posts are in review can be
 * approved in one go.
 */

const REACH_CAMPAIGNS = gql`
  query ReachCampaigns($accountId: ID!) {
    projects(accountId: $accountId) {
      id
      name
      status
      deadline
      campaignStory
      landingPageUrl
      plan
      createdBy
      createdAt
      posts {
        id
        title
        content
        status
        scheduledDate
        publishedAt
        targets {
          id
          platform
        }
      }
    }
    automationRules(accountId: $accountId) {
      id
      name
      description
      isActive
      socialProfileId
    }
    socialProfiles(accountId: $accountId) {
      id
      platform
      profileUsername
    }
  }
`;

interface CampaignPost {
  id: string;
  title: string;
  content: string;
  status: string;
  scheduledDate?: string | null;
  publishedAt?: string | null;
  targets: Array<{ id: string; platform: string }>;
}
interface Campaign {
  id: string;
  name: string;
  status: string;
  deadline?: string | null;
  campaignStory?: string | null;
  landingPageUrl?: string | null;
  plan?: string | null;
  /** Null when the site's agent made it. */
  createdBy?: string | null;
  createdAt: string;
  posts: CampaignPost[];
}
interface Rule {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  socialProfileId: string;
}

const POST_STATUS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Idea', color: 'var(--ink-ink3)' },
  in_review: { label: 'Waiting on you', color: 'var(--ink-amber)' },
  approved: { label: 'Approved', color: 'var(--ink-accent)' },
  scheduled: { label: 'Scheduled', color: 'var(--ink-accent)' },
  published: { label: 'Posted', color: 'var(--ink-green-ink)' },
  cancelled: { label: 'Dropped', color: 'var(--ink-ink3)' },
};

const CAMPAIGN_STATUS: Record<string, string> = {
  planning: 'Planning',
  review: 'In review',
  active: 'Running',
  completed: 'Finished',
};

const day = (v?: string | null) => {
  const d = toDate(v);
  return d ? d.toLocaleDateString([], { month: 'short', day: 'numeric' }) : '';
};

export function ReachCampaigns() {
  const { accountId } = useAccount();
  const { data, refetch } = useQuery<{
    projects: Campaign[];
    automationRules: Rule[];
    socialProfiles: Array<{ id: string; platform: string; profileUsername?: string | null }>;
  }>(REACH_CAMPAIGNS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [approve] = useMutation<{
    approveCampaign: {
      approved: number;
      scheduled: number;
      undated: number;
      skippedChannels: string[];
    };
  }>(APPROVE_CAMPAIGN);
  const [updateRule] = useMutation(UPDATE_AUTOMATION_RULE);
  const [creating, setCreating] = useState(false);
  // ?campaign=<id> (Today's "Review") opens that campaign.
  const [params] = useSearchParams();
  const [sel, setSel] = useState<string | null>(params.get('campaign'));
  const [busy, setBusy] = useState(false);

  const campaigns = useMemo(
    () =>
      [...(data?.projects || [])].sort(
        (a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0)
      ),
    [data]
  );
  const rules = data?.automationRules || [];
  const channels = new Map((data?.socialProfiles || []).map((c) => [c.id, c]));

  // Nothing picked: the first campaign, else the first rule.
  const current = sel ?? campaigns[0]?.id ?? (rules[0] ? `rule:${rules[0].id}` : null);
  const campaign = campaigns.find((c) => c.id === current) || null;
  const rule = current?.startsWith('rule:') ? rules.find((r) => `rule:${r.id}` === current) : null;

  const inReview = (c: Campaign) => c.posts.filter((p) => p.status === 'in_review');

  const approveCampaign = async (c: Campaign) => {
    setBusy(true);
    try {
      const { data: res } = await approve({ variables: { id: c.id } });
      await refetch();
      const r = res?.approveCampaign;
      const parts = [`Approved. ${r?.scheduled || 0} on the calendar.`];
      if (r?.undated)
        parts.push(`${r.undated} ${r.undated === 1 ? 'needs' : 'need'} a date before going out.`);
      if (r?.skippedChannels.length)
        parts.push(
          `${r.skippedChannels.map(channelName).join(', ')} posts need a photo or video, so those were left out.`
        );
      showInkToast(parts.join(' '));
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  const toggleRule = async (r: Rule) => {
    setBusy(true);
    try {
      await updateRule({ variables: { id: r.id, input: { isActive: !r.isActive } } });
      await refetch();
      showInkToast(r.isActive ? `${r.name}: paused.` : `${r.name}: on again.`);
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rcp">
      <aside className="rcp-rail">
        <button
          type="button"
          className="ink-btn ink-btn-quiet rcp-new"
          onClick={() => setCreating(true)}
        >
          New campaign
        </button>
        <div className="pg-rail-head">CAMPAIGNS</div>
        {campaigns.length ? (
          campaigns.map((c) => {
            const waiting = inReview(c).length > 0;
            return (
              <button
                key={c.id}
                type="button"
                className="pg-rail-item"
                data-active={current === c.id ? 'true' : undefined}
                onClick={() => setSel(c.id)}
              >
                <span
                  className="pg-dot"
                  style={{ background: waiting ? 'var(--ink-amber)' : 'var(--ink-line3)' }}
                />
                <span className="pg-rail-text">
                  <span className="pg-rail-title">{c.name}</span>
                  <span className="pg-rail-path">
                    {day(c.createdAt)}
                    {c.deadline ? ` – ${day(c.deadline)}` : ''}
                  </span>
                </span>
                {waiting ? (
                  <span className="pg-rail-tag" style={{ color: 'var(--ink-amber)' }}>
                    Waiting
                  </span>
                ) : null}
              </button>
            );
          })
        ) : (
          <p className="rcp-none">No campaigns yet.</p>
        )}
        <div className="pg-rail-head" style={{ marginTop: 18 }}>
          ALWAYS ON
        </div>
        {rules.length ? (
          rules.map((r) => (
            <button
              key={r.id}
              type="button"
              className="pg-rail-item"
              data-active={current === `rule:${r.id}` ? 'true' : undefined}
              onClick={() => setSel(`rule:${r.id}`)}
            >
              <span
                className="pg-dot"
                style={{ background: r.isActive ? 'var(--ink-green)' : 'var(--ink-line3)' }}
              />
              <span className="pg-rail-text">
                <span className="pg-rail-title">{r.name}</span>
                <span className="pg-rail-path">{r.isActive ? 'Always on' : 'Paused'}</span>
              </span>
            </button>
          ))
        ) : (
          <p className="rcp-none">No always-on rules.</p>
        )}
      </aside>

      <section className="rcp-main">
        {campaign ? (
          <>
            <h2 className="rcp-title">{campaign.name}</h2>
            <div className="rcp-meta">
              {inReview(campaign).length ? (
                <span style={{ color: 'var(--ink-amber)' }}>Waiting on you</span>
              ) : (
                <span>{CAMPAIGN_STATUS[campaign.status] || campaign.status}</span>
              )}
              {campaign.deadline ? <span>· Ends {day(campaign.deadline)}</span> : null}
              {!campaign.createdBy ? <span>· Drafted by your agent</span> : null}
            </div>
            {inReview(campaign).length ? (
              <div className="rcp-banner">
                <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
                <div style={{ flex: 1 }}>
                  <strong>
                    {inReview(campaign).length}{' '}
                    {inReview(campaign).length === 1 ? 'tactic is' : 'tactics are'} waiting for your
                    approval
                  </strong>
                  <div className="ink-muted">
                    Approving puts them on the calendar to go out on their dates.
                  </div>
                </div>
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  disabled={busy}
                  onClick={() => void approveCampaign(campaign)}
                >
                  Approve campaign
                </button>
              </div>
            ) : null}
            {campaign.campaignStory ? <p className="rcp-story">{campaign.campaignStory}</p> : null}
            <div className="rcp-facts">
              {campaign.plan ? (
                <div>
                  <span>Plan</span>
                  <span style={{ whiteSpace: 'pre-wrap' }}>{campaign.plan}</span>
                </div>
              ) : null}
              {campaign.landingPageUrl ? (
                <div>
                  <span>Landing page</span>
                  <a href={campaign.landingPageUrl} target="_blank" rel="noreferrer">
                    {campaign.landingPageUrl.replace(/^https?:\/\//, '')} ↗
                  </a>
                </div>
              ) : null}
              <div>
                <span>Channels</span>
                <span>
                  {[
                    ...new Set(
                      campaign.posts
                        .filter((p) => p.status !== 'cancelled')
                        .flatMap((p) => p.targets.map((t) => channelName(t.platform)))
                    ),
                  ].join(', ') || 'None yet'}
                </span>
              </div>
              {campaign.deadline ? (
                <div>
                  <span>Ends</span>
                  <span>{day(campaign.deadline)}</span>
                </div>
              ) : null}
            </div>
            <div className="rcp-h3-row">
              <h3 className="rcp-h3">
                {campaign.posts.length} {campaign.posts.length === 1 ? 'tactic' : 'tactics'}
              </h3>
              <Link to="/projects/manage" className="ink-quiet-link">
                Add or edit posts
              </Link>
            </div>
            {campaign.posts.length ? (
              <div className="rcp-posts">
                {[...campaign.posts]
                  .sort(
                    (a, b) =>
                      (toDate(a.scheduledDate)?.getTime() ?? Infinity) -
                      (toDate(b.scheduledDate)?.getTime() ?? Infinity)
                  )
                  .map((p) => {
                    const st = POST_STATUS[p.status] || POST_STATUS.draft;
                    const when = toDate(p.publishedAt) || toDate(p.scheduledDate);
                    return (
                      <div className="rcp-post" key={p.id}>
                        <span className="rcp-post-when">{when ? dayTime(when) : 'No date'}</span>
                        <span className="rcp-post-marks">
                          {p.targets.map((t) => (
                            <span
                              key={t.id}
                              className="rc-mark rc-mark-sm"
                              title={channelName(t.platform)}
                            >
                              {channelMark(t.platform)}
                            </span>
                          ))}
                        </span>
                        <span className="rcp-post-text">
                          <span className="rcp-post-idea">{p.title}</span>
                          {p.content && p.content !== p.title ? (
                            <span className="rcp-post-draft">{p.content}</span>
                          ) : null}
                        </span>
                        <span className="rcp-post-status" style={{ color: st.color }}>
                          {st.label}
                        </span>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <p className="ink-muted">
                No tactics yet. Ask your agent for ideas, or add posts yourself.
              </p>
            )}
          </>
        ) : rule ? (
          <>
            <h2 className="rcp-title">{rule.name}</h2>
            <div className="rcp-meta">
              <span style={{ color: rule.isActive ? 'var(--ink-green-ink)' : 'var(--ink-ink3)' }}>
                {rule.isActive ? 'Always on' : 'Paused'}
              </span>
              {channels.get(rule.socialProfileId) ? (
                <span>· {channelName(channels.get(rule.socialProfileId)!.platform)}</span>
              ) : null}
            </div>
            {rule.description ? <p className="rcp-story">{rule.description}</p> : null}
            <div className="rcp-actions">
              <button
                type="button"
                className="ink-btn ink-btn-quiet"
                disabled={busy}
                onClick={() => void toggleRule(rule)}
              >
                {rule.isActive ? 'Pause' : 'Resume'}
              </button>
              <Link to="/automation-rules" className="ink-quiet-link">
                Edit rule
              </Link>
            </div>
          </>
        ) : (
          <div className="rc-empty">
            <div className="rc-empty-title">No campaigns yet</div>
            <p>
              A campaign groups the posts for one push, like a launch or a sale, with its own dates.
              Start one, or ask your agent to plan it.
            </p>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => setCreating(true)}
            >
              New campaign
            </button>
          </div>
        )}
      </section>

      <CreateProjectModal
        open={creating}
        onClose={() => setCreating(false)}
        onSubmit={() => void refetch()}
      />
    </div>
  );
}
